import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatQty } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { Toolbar } from '../ui/Page';
import { SearchInput } from '../ui/SearchInput';
import { DataTable, dash, type DataTableColumn } from '@/components/ui/data-table';
import { SectionCard } from '../ui/Detail';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { money } from '../money';
import { StockSheet } from './StockSheet';
import type { OnHandItem, StockLocation } from './types';
import { IconInventory, IconPlus } from '../icons';

/**
 * What's on the shelf. The table answers the only two questions a shop owner asks out
 * loud: how many do I have, and where are they. Per-location columns would have been the
 * obvious layout and it breaks at the tenth van; a "where" column of chips reads the same
 * at any size and keeps the table inside the ≤7-column law. The full breakdown, the ledger
 * and the actions live one row-click away.
 */
export function StockTab({
  items,
  locations,
  onOrder,
}: {
  items: OnHandItem[];
  locations: StockLocation[];
  onOrder: Map<string, number>;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const priceBook = useQuery(trpc.priceBook.list.queryOptions());
  const [search, setSearch] = useState('');
  // Keyed by ID, not by the row object: a count taken inside the sheet has to show up in
  // it. Holding the row leaves the panel insisting the books say 0 right after someone
  // told it there are 12.
  const [openId, setOpenId] = useState<string | null>(null);

  const setTracking = useMutation(
    trpc.inventory.setTracking.mutationOptions({
      onSuccess: () => void qc.invalidateQueries(),
    }),
  );

  const locationName = useMemo(
    () => new Map(locations.map((l) => [l.id, l.name])),
    [locations],
  );

  const columns: DataTableColumn<OnHandItem>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Part',
        value: (i) => i.name,
        cell: (i) => (
          <div>
            <p className="font-medium">{i.name}</p>
            {i.unitLabel ? (
              <p className="text-xs text-muted-foreground">per {i.unitLabel}</p>
            ) : null}
          </div>
        ),
      },
      { key: 'sku', label: 'SKU', value: (i) => i.sku, cell: (i) => dash(i.sku) },
      {
        key: 'onHand',
        label: 'On hand',
        align: 'right',
        value: (i) => i.totalHundredths,
        searchValue: () => null,
        cell: (i) => (
          <span
            className={`font-semibold tabular-nums ${
              i.totalHundredths < 0 ? 'text-[var(--sw-danger-text)]' : ''
            }`}
          >
            {formatQty(i.totalHundredths)}
          </span>
        ),
      },
      {
        key: 'onOrder',
        label: 'On order',
        align: 'right',
        value: (i) => onOrder.get(i.id) ?? 0,
        searchValue: () => null,
        // What's coming stops the second order for the same part — the single most common
        // way a small shop buys the same compressor twice.
        cell: (i) => {
          const coming = onOrder.get(i.id) ?? 0;
          return coming === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className="tabular-nums text-[var(--sw-brand-text)]">
              +{formatQty(coming)}
            </span>
          );
        },
      },
      {
        key: 'where',
        label: 'Where',
        searchValue: (i) =>
          i.cells.map((c) => locationName.get(c.locationId) ?? '').join(' '),
        cell: (i) => {
          // Only places holding stock in their own right — a warehouse whose count comes
          // entirely from its bins would otherwise appear twice in one line.
          const held = i.cells.filter((c) => c.ownHundredths !== 0);
          if (held.length === 0)
            return <span className="text-muted-foreground">Nowhere yet</span>;
          return (
            <div className="flex flex-wrap gap-1">
              {held.map((c) => (
                <StatusBadge
                  key={c.locationId}
                  tone={c.ownHundredths < 0 ? 'danger' : 'neutral'}
                >
                  {locationName.get(c.locationId) ?? 'Unknown'}{' '}
                  {formatQty(c.ownHundredths)}
                </StatusBadge>
              ))}
            </div>
          );
        },
      },
      {
        key: 'value',
        label: 'Value',
        align: 'right',
        value: (i) => (i.avgCostCents ?? 0) * i.totalHundredths,
        searchValue: () => null,
        cell: (i) =>
          // No cost until something has been received at a price. A guess here would
          // become somebody's balance sheet.
          // `== null`: a cost withheld from this caller (047) reads the same dash.
          i.avgCostCents == null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className="tabular-nums">
              {money(Math.round((i.avgCostCents * i.totalHundredths) / 100))}
            </span>
          ),
      },
    ],
    [locationName, onOrder],
  );

  const openItem = openId ? (items.find((i) => i.id === openId) ?? null) : null;
  // Gentler than the others — the section HIDES rather than claiming zero — but it is the
  // same absent answer, and a shop looking for a part it deliberately does not count sees
  // no section and concludes the feature is gone.
  const priceBookState = shownState(priceBook, priceBook.data);
  const untracked = (priceBook.data ?? []).filter(
    (i) => !i.tracked && (i.kind === 'material' || i.kind === 'service'),
  );

  return (
    <>
      <Toolbar>
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search parts"
          aria-label="Search parts"
        />
      </Toolbar>

      <DataTable
        tableId="stock"
        columns={columns}
        rows={items}
        rowKey={(i) => i.id}
        search={search}
        onRowClick={(i) => setOpenId(i.id)}
        rowLabel={(i) => `Open ${i.name}`}
        empty={
          <EmptyState
            icon={IconInventory}
            title="Nothing counted yet"
            description="Pick the parts worth counting below — compressors and capacitors, not wire nuts — then take a count."
          />
        }
      />

      {priceBookState !== 'ready' ? (
        <SectionCard title="Not counted">
          <UnresolvedState
            state={priceBookState}
            what="the rest of your price book"
            icon={IconInventory}
            error={priceBook.error}
            onRetry={() => void priceBook.refetch()}
            skeleton="h-10"
          />
        </SectionCard>
      ) : untracked.length > 0 ? (
        <SectionCard title={`Not counted (${untracked.length})`}>
          <StaleNotice
            show={refreshFailed(priceBook, priceBook.data)}
            onRetry={() => void priceBook.refetch()}
          />
          <p className="mb-3 text-xs text-muted-foreground">
            Everything else in your price book. Count only what's worth counting — a shop
            that tries to track wire nuts stops tracking anything.
          </p>
          <ul className="divide-y divide-border text-sm">
            {untracked.map((i) => (
              <li key={i.id} className="flex items-center gap-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{i.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {money(i.unitPriceCents)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  disabled={setTracking.isPending}
                  onClick={() =>
                    setTracking.mutate({ itemId: i.id, tracked: true, ...idem() })
                  }
                >
                  <IconPlus size={14} />
                  Start counting
                </Button>
              </li>
            ))}
          </ul>
        </SectionCard>
      ) : null}

      {openItem ? (
        <StockSheet
          item={openItem}
          locations={locations}
          onClose={() => setOpenId(null)}
        />
      ) : null}
    </>
  );
}
